import React, { useState, useRef, useEffect } from "react";
import { Bell, BellDot } from "lucide-react";
import { useNotification } from "../../../contexts/notification/NotificationContext";

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } =
    useNotification();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      // When opening, mark visible notifications as read
      const notificationIds = notifications
        .filter((notif) => !notif.isRead)
        .slice(0, 5)
        .map((notif) => notif._id);

      if (notificationIds.length > 0) {
        markAsRead(notificationIds);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Prevent body scroll when modal is open on mobile
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "alert":
        return "🔔";
      case "success":
        return "✅";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "📢";
    }
  };

  // Format notification time (replacing date-fns with a simple implementation)
  const formatNotificationTime = (createdAt) => {
    try {
      const date = new Date(createdAt);
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);

      if (diffSec < 60) return "just now";
      if (diffMin < 60)
        return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
      if (diffHour < 24)
        return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
      if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;

      // Fallback to simple date formatting for older dates
      return date.toLocaleDateString();
    } catch (error) {
      return "recently";
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center rounded-full w-10 h-10 relative hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
        aria-label="Notifications"
      >
        {unreadCount > 0 ? (
          <>
            <BellDot className="w-5 h-5 text-gray-700" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-sm">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          </>
        ) : (
          <Bell className="w-5 h-5 text-gray-700" />
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop - Full screen on mobile, hidden on desktop */}
          <div
            className="fixed inset-0 bg-black/40 z-[9998] md:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Notification Modal */}
          <div
            className="
              fixed md:absolute
              top-20 left-4 right-4
              md:top-full md:left-auto md:right-0 md:translate-x-0 md:translate-y-0 md:mt-2
              w-auto md:w-96
              max-h-[70vh] md:max-h-96
              bg-white rounded-2xl md:rounded-lg shadow-2xl overflow-hidden 
              z-[9999] 
              border border-gray-200
              flex flex-col
            "
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-800">
                Notifications
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      markAllAsRead();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark all read
                  </button>
                )}
                {/* Close button - only on mobile */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="md:hidden p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {loading ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                  </div>
                  <p className="mt-3 text-sm">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-12 text-center text-gray-500">
                  <Bell className="mx-auto w-12 h-12 text-gray-300" />
                  <p className="mt-3 font-medium">No notifications yet</p>
                  <p className="text-xs mt-1">
                    New notifications will appear here
                  </p>
                </div>
              ) : (
                <div>
                  {notifications.slice(0, 5).map((notification) => (
                    <div
                      key={notification._id}
                      className={`px-4 py-3 border-b hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                        !notification.isRead ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="text-lg flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <span className="text-xs text-gray-400 mt-1 block">
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 text-center border-t bg-gray-50 shrink-0">
              <button
                onClick={() => {
                  window.location.href = "/notifications";
                  setIsOpen(false);
                }}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              >
                View all notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;
